import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentFile, setOpenFiles } from "../slices/appSlice";
// Show directory and files navigation as window explorer
// we aer using flowbite css framework for styling
import { FileItem } from "../types/projectTypes";
import { Dialogs } from "./Dialog";
import { useTranslation } from "react-i18next";
import {
  Button,
  Label,
  Select,
  TextInput,
  Toast,
  Tooltip,
} from "flowbite-react";
import useProjects from "../hooks/useProjects";
import {
  HiFolderAdd,
  HiDocumentAdd,
  HiRefresh,
  HiChevronDown,
  HiChevronRight,
  HiPhotograph,
  HiPencil,
  HiOutlineSearch,
  HiOutlineSortAscending,
} from "react-icons/hi";
import { useMemo } from "react";
import { server } from "../config";

const flattenDirectories = (nodes: FileItem[] | undefined): FileItem[] => {
  if (!nodes) return [];
  const result: FileItem[] = [];
  nodes.forEach((node) => {
    if (node.kind === "directory") {
      result.push(node);
    }
    if (node.children?.length) {
      result.push(...flattenDirectories(node.children));
    }
  });
  return result;
};

const DirList = ({
  dirs,
  hasParent,
}: {
  dirs: FileItem[];
  hasParent?: boolean;
}) => {
  const { openFiles } = useSelector((state: any) => state.app);
  const dispatch = useDispatch();
  const initialStates = {
    newFile: false,
    newFolder: false,
    uploadFile: false,
    syncFiles: false,
    renameFile: false,
    imagesView: false,
    currentDialog: "",
    showImageCopiedToast: false,
  };
  const [dirListState, setDirListState] = React.useState({
    expandedFolders: [] as string[],
    ...initialStates,
  });
  const [folderName, setFolderName] = React.useState("");
  const [folderParentId, setFolderParentId] = React.useState("root");
  const [renameValue, setRenameValue] = React.useState("");
  const [selectedImages, setSelectedImages] = React.useState<File[]>([]);
  const [imagesSearch, setImagesSearch] = React.useState("");
  const [imagesSortBy, setImagesSortBy] = React.useState<"name" | "created" | "updated">("updated");
  const [imagesSortDir, setImagesSortDir] = React.useState<"asc" | "desc">("desc");
  const { t } = useTranslation();
  const { addProjectFile, renameProjectFile, uploadProjectImages, getProjectImages } =
    useProjects();
  const { currentProject, currentFile } = useSelector((state: any) => state.app);

  const projectDirectories = useMemo(
    () => flattenDirectories(currentProject?.files as FileItem[]),
    [currentProject?.files],
  );

  const projectImages = useMemo(
    () => ((currentProject as any)?.images || []) as any[],
    [currentProject],
  );

  const filteredSortedImages = useMemo(() => {
    const search = imagesSearch.trim().toLowerCase();
    const filtered = projectImages.filter((image: any) => {
      const searchable = `${image.name || ""} ${image.description || ""}`.toLowerCase();
      return !search || searchable.includes(search);
    });

    return [...filtered].sort((a: any, b: any) => {
      const direction = imagesSortDir === "asc" ? 1 : -1;
      if (imagesSortBy === "name") {
        return ((a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase())) * direction;
      }
      const aValue = Date.parse(a[imagesSortBy] || "");
      const bValue = Date.parse(b[imagesSortBy] || "");
      const safeA = Number.isNaN(aValue) ? 0 : aValue;
      const safeB = Number.isNaN(bValue) ? 0 : bValue;
      return (safeA - safeB) * direction;
    });
  }, [imagesSearch, imagesSortBy, imagesSortDir, projectImages]);

  React.useEffect(() => {
    if (dirListState.currentDialog === "uploadFile" || dirListState.currentDialog === "imagesView") {
      getProjectImages(currentProject.id);
    }
  }, [dirListState.currentDialog, currentProject.id, getProjectImages]);

  React.useEffect(() => {
    if (dirListState.currentDialog === "renameFile" && currentFile?.name) {
      setRenameValue(currentFile.name);
    }
  }, [dirListState.currentDialog, currentFile?.name]);

  const renderFilesMenu = () => {
    return (
      <div className="mb-2 flex flex-wrap gap-1">
        <div className="flex gap-1">
          <Tooltip content={t("newFolder")}>
            <Button
              onClick={() => {
                setDirListState({
                  ...initialStates,
                  ...dirListState,
                  newFolder: true,
                  currentDialog: "newFolder",
                });
              }}
              gradientMonochrome={"info"}
              size={"sm"}
              type="button"
            >
              <HiFolderAdd />
            </Button>
          </Tooltip>

          <Tooltip content={t("newFile")}>
            <Button
              onClick={() => {
                setDirListState({
                  ...initialStates,
                  ...dirListState,
                  newFile: true,
                  currentDialog: "newFile",
                });
              }}
              size={"sm"}
              gradientMonochrome={"success"}
              type="button"
            >
              <HiDocumentAdd />
            </Button>
          </Tooltip>
        </div>
        <div>
          <Tooltip content={t("syncFiles")}>
            <Button
              onClick={() => {
                setDirListState({
                  ...initialStates,
                  ...dirListState,
                  syncFiles: true,
                  currentDialog: "syncFiles",
                });
              }}
              size={"sm"}
              gradientMonochrome={"purple"}
              type="button"
            >
              <HiRefresh />
            </Button>
          </Tooltip>
        </div>
        <div>
          <Tooltip content={t("uploadFile")}>
            <Button
              onClick={() => {
                setDirListState({
                  ...initialStates,
                  ...dirListState,
                  uploadFile: true,
                  currentDialog: "uploadFile",
                });
              }}
              size={"sm"}
              gradientMonochrome={"success"}
              type="button"
            >
              <HiPhotograph />
            </Button>
          </Tooltip>
        </div>
        <div>
          <Tooltip content={t("rename") || "Rename"}>
            <Button
              onClick={() => {
                setDirListState({
                  ...initialStates,
                  ...dirListState,
                  renameFile: true,
                  currentDialog: "renameFile",
                });
              }}
              disabled={!currentFile}
              size="sm"
              color="gray"
              type="button"
            >
              <HiPencil />
            </Button>
          </Tooltip>
        </div>
        <div>
          <Tooltip content={t("images") || "Images"}>
            <Button
              onClick={() => {
                setDirListState({
                  ...initialStates,
                  ...dirListState,
                  imagesView: true,
                  currentDialog: "imagesView",
                });
              }}
              size="sm"
              color="gray"
              type="button"
            >
              <HiPhotograph />
            </Button>
          </Tooltip>
        </div>
      </div>
    );
  };

  return (
    <aside
      id="dir-list"
      className={`${!hasParent ? "overflow-x-auto" : ""} size-full overflow-y-auto bg-gray-100 p-2 dark:bg-gray-800`}
    >
      {!hasParent && (
        <div className="flex items-center gap-2">
          <Dialogs
            dialogs={[
              {
                id: "renameFile",
                title: (
                  <div className="flex items-center gap-2">
                    <HiPencil />
                    {t("rename") || "Rename"}
                  </div>
                ),
                body: (
                  <form
                    className="flex flex-col gap-3"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      if (!currentFile?.id || !renameValue.trim()) return;
                      const renamed = await renameProjectFile(
                        currentFile.id,
                        renameValue.trim(),
                      );
                      if (renamed) {
                        dispatch(setCurrentFile({ ...currentFile, name: renameValue.trim() }));
                      }
                      setDirListState({
                        ...dirListState,
                        ...initialStates,
                        currentDialog: "",
                      });
                    }}
                  >
                    <Label>{t("newName") || "New name"}</Label>
                    <TextInput
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      placeholder={t("newName") || "New name"}
                    />
                    <Button type="submit" color="blue">
                      {t("rename") || "Rename"}
                    </Button>
                  </form>
                ),
              },
              {
                id: "imagesView",
                title: t("images") || "Project images",
                body: (
                  <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                      <div className="flex items-center gap-2 rounded border border-gray-300 px-3 py-2">
                        <HiOutlineSearch className="text-gray-500" />
                        <input
                          className="w-full bg-transparent outline-none"
                          value={imagesSearch}
                          onChange={(e) => setImagesSearch(e.target.value)}
                          placeholder={t("search") || "Quick search"}
                        />
                      </div>
                      <Select
                        value={imagesSortBy}
                        onChange={(e) => setImagesSortBy(e.target.value as any)}
                      >
                        <option value="updated">{t("updated") || "Updated"}</option>
                        <option value="created">{t("created") || "Created"}</option>
                        <option value="name">{t("name") || "Name"}</option>
                      </Select>
                      <Button
                        type="button"
                        color="gray"
                        onClick={() =>
                          setImagesSortDir((prev) => (prev === "asc" ? "desc" : "asc"))
                        }
                      >
                        <HiOutlineSortAscending />
                        {imagesSortDir === "asc" ? "ASC" : "DESC"}
                      </Button>
                    </div>
                    <div className="flex flex-col gap-2 rounded border border-gray-300 p-3">
                      <Label>{t("uploadImage") || "Upload images"}</Label>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => setSelectedImages(Array.from(e.target.files || []))}
                      />
                      <Button
                        type="button"
                        color="blue"
                        onClick={async () => {
                          if (!currentProject?.id || selectedImages.length === 0) return;
                          await uploadProjectImages(currentProject.id, selectedImages);
                          await getProjectImages(currentProject.id);
                          setSelectedImages([]);
                        }}
                      >
                        {t("upload") || "Upload"}
                      </Button>
                    </div>
                    <div className="max-h-[460px] overflow-y-auto">
                      {filteredSortedImages.length === 0 ? (
                        <div className="rounded border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
                          {t("noImages") || "No images found"}
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-5">
                          {filteredSortedImages.map((image: any) => {
                            const imagePath = image.thumb || image.medium || image.image;
                            const imageUrl = `${server}/djmedia/${imagePath}`;
                            return (
                              <div key={image.id} className="overflow-hidden rounded border border-gray-200 bg-white">
                                <img src={imageUrl} alt={image.name || "project image"} className="h-28 w-full object-cover" />
                                <div className="flex flex-col gap-1 p-2 text-xs">
                                  <div className="truncate font-semibold text-gray-700">{image.name || `Image ${image.id}`}</div>
                                  <div className="truncate text-gray-500">{image.updated || image.created || ""}</div>
                                  <button
                                    type="button"
                                    className="mt-1 rounded bg-slate-100 px-2 py-1 text-slate-700 hover:bg-slate-200"
                                    onClick={() => navigator.clipboard.writeText(imageUrl)}
                                  >
                                    {t("copyUrl") || "Copy URL"}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                ),
              },
              {
                id: "uploadFile",
                title: t("uploadFile"),
                body: (
                  <div className="flex flex-col items-center gap-2">
                    {/* show two tabs first to upload, second list images from project in gallery view */}
                    <div className="flex gap-2">
                      <HiDocumentAdd />
                      {t("uploadFile")}
                      <form
                        className="ml-4"
                        onSubmit={async (e) => {
                          e.preventDefault();
                          var fileInput = document.getElementById("file") as HTMLInputElement;
                          if (!fileInput || !fileInput.files) {
                            return;
                          }
                          var file = fileInput.files[0];
                          if (file) {
                            await uploadProjectImages(currentProject.id, [
                              file,
                            ]);
                          }
                        }}
                      >
                        <input
                          type="file"
                          accept="image/*"
                          name="file"
                          id="file"
                        />
                        <Button type="submit">{t("upload")}</Button>
                      </form>
                    </div>
                    <div className="w-full">
                      <div className="flex flex-wrap">
                        {currentProject.images &&
                        currentProject.images.length > 0 ? (
                          currentProject.images.map((image: any) => (
                            // onClick copy to clipboard the url with server
                            <div
                              key={image.id}
                              className="m-2"
                              style={{
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  `${server}/djmedia/${image.image}`,
                                );
                                // show a success message
                                setDirListState({
                                  ...dirListState,
                                  showImageCopiedToast: true,
                                });
                                setTimeout(() => {
                                  setDirListState({
                                    ...dirListState,
                                    showImageCopiedToast: false,
                                  });
                                }, 2000);
                              }}
                            >
                              <img
                                src={`${server}/djmedia/${image.image}`}
                                alt={image.name}
                                className="size-32 object-cover"
                              />
                            </div>
                          ))
                        ) : (
                          <div className="text-gray-500">{t("noImages")}</div>
                        )}
                        {dirListState.showImageCopiedToast && (
                          <Toast>
                            <div className="text-sm">
                              {t("imagePathCopied")}
                            </div>
                          </Toast>
                        )}
                      </div>
                    </div>
                  </div>
                ),
                
              },
              {
                id: "newFolder",
                title: (
                  <div className="flex items-center gap-2">
                    <HiFolderAdd />
                    {t("newFolder")}
                  </div>
                ),
                body: (
                  <form
                    className="flex flex-col gap-3"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const normalizedName = folderName.trim();
                      if (!normalizedName) return;

                      let parentId: number | undefined;
                      let parentPath = "/";
                      if (folderParentId !== "root") {
                        parentId = parseInt(folderParentId, 10);
                        const parentDirectory = projectDirectories.find((dir) => dir.id === parentId);
                        parentPath = parentDirectory?.path || "/";
                      }

                      await addProjectFile({
                        id: 0,
                        name: normalizedName,
                        kind: "directory",
                        path: parentPath,
                        ...(parentId ? { parentId } : {}),
                      } as FileItem);
                      setFolderName("");
                      setFolderParentId("root");
                      setDirListState({
                        ...dirListState,
                        ...initialStates,
                        currentDialog: "",
                      });
                    }}
                  >
                    <Label>{t("folderName") || "Folder name"}</Label>
                    <TextInput
                      value={folderName}
                      onChange={(e) => setFolderName(e.target.value)}
                      placeholder={t("folderName") || "Folder name"}
                    />
                    <Label>{t("parentFolder") || "Parent folder"}</Label>
                    <Select
                      value={folderParentId}
                      onChange={(e) => setFolderParentId(e.target.value)}
                    >
                      <option value="root">/ ({t("root") || "Root"})</option>
                      {projectDirectories.map((dir) => (
                        <option key={dir.id} value={dir.id}>
                          {`${dir.path || "/"}/${dir.name}`.replace("//", "/")}
                        </option>
                      ))}
                    </Select>
                    <Button type="submit" color="blue">
                      {t("createFolder") || "Create folder"}
                    </Button>
                  </form>
                ),
              },
              {
                id: "newFile",
                title: (
                  <div className="flex items-center gap-2">
                    <HiDocumentAdd />
                    {t("newFile")}
                  </div>
                ),
                body: (
                  <div className=" container flex flex-col gap-2">
                    <form
                      id="newFileForm"
                      className="flex flex-col gap-2"
                      onSubmit={async (e) => {
                        e.preventDefault();
                        var fileName = (
                          document.getElementById(
                            "fileName",
                          ) as HTMLInputElement
                        ).value;
                        var fileType = (
                          document.getElementById(
                            "fileType",
                          ) as HTMLSelectElement
                        ).value;
                        var path = "";
                        if (fileName.includes("/")) {
                          path = fileName.substring(
                            0,
                            fileName.lastIndexOf("/"),
                          );
                          fileName = fileName.substring(
                            fileName.lastIndexOf("/") + 1,
                          );
                        }
                        if (fileType !== "") {
                          if (!fileName.endsWith(`.${fileType}`)) {
                            fileName += `.${fileType}`;
                          }
                        }
                        var newFile = await addProjectFile({
                          id: 0,
                          name: fileName,
                          path: path,
                          kind: "file",
                        });
                        if (newFile) {
                          var d = {} as Record<string, any>;
                          Object.keys(newFile).forEach((key: string) => {
                            // Change the parameter type to string
                            if (key !== "entry" && key !== "blob") {
                              // Remove unnecessary type assertion
                              d[key] = newFile[key as keyof FileItem];
                            }
                          });
                          if (
                            !openFiles.find(
                              (f: FileItem) => f.id === newFile.id,
                            )
                          ) {
                            dispatch(setOpenFiles([...openFiles, d]));
                          }
                          dispatch(setCurrentFile(d));
                        }
                        setDirListState({
                          ...dirListState,
                          ...initialStates,
                          currentDialog: "",
                        });
                      }}
                    >
                      <div className="flex flex-col gap-2">
                        <Label>{t("fileName")}</Label>
                        <TextInput
                          id="fileName"
                          placeholder={t("fileName")}
                          type="text"
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <Label>{t("fileType")}</Label>
                        <Select id="fileType" defaultValue="psmdoc">
                          <option value="psmdoc">PsmDoc</option>
                          <option value="txt">Text</option>
                          <option value="json">JSON</option>
                          <option value="">Plain Text</option>
                        </Select>
                      </div>
                      <Button
                        type="submit"
                        gradientMonochrome={"success"}
                        size="md"
                        className="flex w-full items-center justify-center gap-2 text-center align-middle"
                      >
                        <HiDocumentAdd />
                        {t("createFile")}
                      </Button>
                    </form>
                  </div>
                ),
              },
              {
                id: "syncFiles",
                title: (
                  <div className="flex items-center gap-2">
                    <HiRefresh />
                    {t("syncFiles")}
                  </div>
                ),
                body: <div>Content</div>,
              },
            ]}
            currentDialog={dirListState.currentDialog}
            onClose={(id: string) => {
              setDirListState({
                ...dirListState,
                ...initialStates,
                currentDialog: "",
              });
            }}
          />
          {/* add nav icons menu to new folder and file */}
          {/* /* use flowbite framework  */}
          {renderFilesMenu()}
        </div>
      )}{" "}
      {/* end of hasParent */}
      {/* add border to left */}
      <ul
        className={`${hasParent ? "gap-1 border-l border-gray-200 pl-1" : ""} overflow-x-hidden p-0`}
      >
        {dirs?.map((dir, index) => {
          var liClass =
            "text-nowrap text-sm dark:text-gray-300 cursor-pointer ";
          if (dir.kind === "directory") {
            liClass += "ml-1 text-blue-500 text-sm dark:text-blue-300";
          } else {
            liClass = "ml-1 text-green-500 text-sm";
          }
          if (dir.kind === "file") {
            liClass = "ml-1 text-gray-500 text-sm cursor-pointer";
            if (dir.name.endsWith(".psmdoc")) {
              liClass += " dark:text-green-500 text-green-500";
            }
          }

          return (
            <li
              key={index}
              className={`${liClass}`}
              onContextMenu={(e) => {
                e.preventDefault();
                console.log("Right click");
              }}
            >
              <span
                className="flex items-center gap-2"
                onClick={() => {
                  if (
                    dir.children &&
                    dir.children.length > 0 &&
                    dir.kind === "directory"
                  ) {
                    setDirListState({
                      ...dirListState,
                      expandedFolders: dirListState.expandedFolders.includes(
                        dir.name,
                      )
                        ? dirListState.expandedFolders.filter(
                            (folder) => folder !== dir.name,
                          )
                        : [...dirListState.expandedFolders, dir.name],
                    });
                  } else {
                    if (dir.kind === "file") {
                      var d = {} as Record<string, any>;
                      Object.keys(dir).forEach((key: string) => {
                        // Change the parameter type to string
                        if (key !== "entry" && key !== "blob") {
                          // Remove unnecessary type assertion
                          d[key] = dir[key as keyof FileItem];
                        }
                      });
                      if (!openFiles.find((f: FileItem) => f.id === dir.id)) {
                        dispatch(setOpenFiles([...openFiles, d]));
                      }
                      dispatch(setCurrentFile(d));
                    }
                  }
                }}
              >
                {dir.kind === "directory" && (
                  <span className="mr-2 ">
                    {dirListState.expandedFolders.includes(dir.name) ? (
                      <span>
                        <HiChevronDown />
                      </span>
                    ) : (
                      <span>
                        <HiChevronRight />
                      </span>
                    )}
                  </span>
                )}

                {dir.name}
              </span>
              {dir.children &&
                dirListState.expandedFolders.includes(dir.name) && (
                  <DirList dirs={dir.children} hasParent={true} />
                )}
            </li>
          );
        })}
      </ul>
    </aside>
  );
};

export default DirList;
